import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { Recurrence } from "@app/shared/models/recurrence.model";
import { Log } from "ng2-logger";
import { recurrenceTypes } from "@app/shared/models/consts/recurrenceTypes.const";
import { recurrenceUnits } from "@app/shared/models/consts/recurrenceUnits.const";

@Component({
    selector: "app-recurrence-details-form",
    templateUrl: "./recurrence-details-form.component.html",
    styleUrls: ["./recurrence-details-form.component.scss"],
})
export class RecurrenceDetailsFormComponent implements OnInit {
    _logger = Log.create(RecurrenceDetailsFormComponent.name);

    @Input() loading = false;
    @Input() recurrence: any;
    @Input() showValidations = false;

    @Output() create = new EventEmitter<any>();
    @Output() edit = new EventEmitter<any>();
    @Output() delete = new EventEmitter<any>();
    @Output() close = new EventEmitter<any>();

    public detailsForm: UntypedFormGroup;

    mode = "create";
    recurrenceTypes = [];
    units = [];

    constructor() {}

    ngOnInit() {
        this._logger.info("ngOnInit:: Preparing recurrence details form");
        this.detailsForm = new UntypedFormGroup({
            name: new UntypedFormControl("", Validators.required),
            type: new UntypedFormControl("", Validators.required),
            value: new UntypedFormControl("", [Validators.required, Validators.pattern("^[1-9][0-9]*$")]),
            unit: new UntypedFormControl("", Validators.required),
        });
        this.recurrenceTypes = recurrenceTypes;
        this.units = recurrenceUnits;
        this.recurrence = new Recurrence();
    }

    init(recurrence: Recurrence, mode: string) {
        if (recurrence) {
            this.recurrence = recurrence;
            this.mode = mode;
            this.loadFormValue();
        }
    }

    private loadFormValue() {
        if (!this.recurrence) { return; }
        const { name, type, value, unit } = this.recurrence;

        this.detailsForm.setValue({ name, type, value, unit});
      }

    resetForm(): void {
        this._logger.info("resetForm:: Resetting recurrence details form");
        this.detailsForm.reset();
    }

    getFormDetails() {
        this._logger.info("getFormDetails::");
        const formData = this.detailsForm.getRawValue();
        return formData;
    }

    onTypeUpdate(data): void {
        this._logger.info("onTypeUpdate:: Updating recurrence type", data);
        this.recurrence.type = data.id;
    }

    onUnitUpdate(data): void {
        this._logger.info("onUnitUpdate:: Updating recurrence unit", data);
        this.recurrence.unit = data.id;
    }

    setFormValidators() {
        this._logger.info("setFormValidators:: Setting recurrence details form validators for recurrence details form", this.detailsForm);
        switch (this.detailsForm.controls.type.value) {
            case 1:
                this.detailsForm.controls.value.setValidators([]);
                this.detailsForm.controls.unit.setValidators([]);
                this.detailsForm.controls.unit.updateValueAndValidity();
                this.detailsForm.controls.value.updateValueAndValidity();
                break;
            case 2:
                this.detailsForm.controls.value.setValidators([Validators.required, Validators.pattern("^[1-9][0-9]*$")]);
                this.detailsForm.controls.unit.setValidators([Validators.required]);
                this.detailsForm.controls.unit.updateValueAndValidity();
                this.detailsForm.controls.value.updateValueAndValidity();
                break;
            case 3:
                this.detailsForm.controls.value.setValidators([Validators.required, Validators.pattern("^[1-9][0-9]*$")]);
                this.detailsForm.controls.unit.setValidators([]);
                this.detailsForm.controls.unit.updateValueAndValidity();
                this.detailsForm.controls.value.updateValueAndValidity();
                break;
            case 4:
                this.detailsForm.controls.value.setValidators([]);
                this.detailsForm.controls.unit.setValidators([]);
                this.detailsForm.controls.unit.updateValueAndValidity();
                this.detailsForm.controls.value.updateValueAndValidity();
                break;
        }
    }

    onConfirm() {
        this._logger.info("onConfirm:: Confirming recurrence details form and sending data to parent component", this.detailsForm);
        this.setFormValidators();
        if (!this.detailsForm.valid) {
            this.showValidations = true;
            return;
        }
        this.showValidations = false;

        const formData = Object.assign(
            {},
            this.detailsForm.value
        );
        formData.id = this.recurrence.id;
        this.mode === 'create' ? this.create.emit(formData) : this.edit.emit(formData);
    }

    onDelete() {
        this._logger.info("onDelete:: Deleting recurrence", this.detailsForm);
        this.delete.emit(this.recurrence);
    }

    onClose() {
        this._logger.info("onClose:: Closing recurrence details form and sending close event to parent component");
        this.close.emit();
    }

    get type() {
        return ["Empty", "Return", "Time",  "Cycle", "On Demand"][this.recurrence.type || 0];
      }

    get unit() {
        return ["Empty", "Days", "Weeks", "Months"][this.recurrence.unit || 0];
    }
}
